import { useState } from "react";
import { toast } from "sonner";
import { Download, Copy, ExternalLink, Sparkles, Image as ImageIcon } from "lucide-react";
import { CREATOR_ADS, MEMBER_ADS, AD_FORMATS, adVariant, buildAd, adUrl, downloadHtml, downloadImage, adImageUrl } from "@/lib/creatorAds";
import { ShareBar } from "@/components/ShareBar";
import { CaptionEditor, DEFAULT_CAPTION, fullCaption } from "@/components/CaptionEditor";

const input = "bg-transparent border-b border-white/15 py-2 text-sm outline-none focus:border-gold w-full";
const btn = "flex-1 flex items-center justify-center gap-1 px-2 py-2.5 rounded-full text-[10px] uppercase tracking-[0.1em] whitespace-nowrap transition-colors";

const KINDS = {
  creator: { ads: CREATOR_ADS, path: "/join", fallback: "creator-launch", title: "Creator recruitment ads", testId: "creator-ads",
    note: "The button sends people to the creator sign-up page. Everyone who joins is listed under Models." },
  member: { ads: MEMBER_ADS, path: "/vip", fallback: "member-launch", title: "Member ads: free Premium for the first 300", testId: "member-ads",
    note: "The button sends people to member sign-up and then to Basic checkout. The first 300 who activate Basic get Premium free for 1 month. They're listed under Users." },
};

const Preview = ({ ad, url, p, id }) => (
  <a href={url} target="_blank" rel="noopener noreferrer" data-testid={`${p}-preview-${id}`} className={`relative block w-full ${ad.w === ad.h ? "max-w-[300px]" : "max-w-[260px]"} mx-auto rounded-xl overflow-hidden group`}>
    <img src={adImageUrl(ad)} alt={ad.label} className="w-full h-auto block" />
    <span className="absolute rounded-full ring-2 ring-gold/0 group-hover:ring-gold/80 group-hover:shadow-[0_0_24px_rgba(212,175,55,0.6)] transition-shadow"
      style={{ left: `${ad.btn.left}%`, top: `${ad.btn.top}%`, width: `${ad.btn.width}%`, height: `${ad.btn.height}%` }} />
  </a>
);

const AdCard = ({ ad: base, url, kind, p, path, campaign, store }) => {
  const adKey = `${kind}-${base.id}`;
  const [caption, setCaption] = useState(store?.captions[adKey] || DEFAULT_CAPTION[kind]);
  const [fmt, setFmt] = useState("poster");
  const ad = adVariant(base, fmt);
  const code = buildAd(ad, url, kind);
  const copy = () => navigator.clipboard.writeText(code).then(() => toast.success("Ad code copied")).catch(() => toast.error("Copy blocked by the browser"));
  return (
    <div data-testid={`${p}-${base.id}`} className="rounded-2xl border border-white/8 bg-ink/60 p-4 space-y-4">
      <p className="text-[10px] uppercase tracking-[0.2em] text-text-muted">{base.label}</p>
      <div className="flex gap-1 rounded-full border border-white/10 p-1 w-fit">
        {AD_FORMATS.map((f) => (
          <button key={f.id} data-testid={`${p}-format-${base.id}-${f.id}`} onClick={() => setFmt(f.id)}
            className={`px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.12em] transition-colors ${fmt === f.id ? "bg-gold text-ink" : "text-text-muted hover:text-gold"}`}>{f.id}</button>
        ))}
      </div>
      <Preview ad={ad} url={url} p={p} id={base.id} />
      <div className="flex flex-wrap gap-2">
        <button data-testid={`${p}-download-${base.id}`} onClick={() => downloadHtml(`velluto-${kind}-ad-${ad.id}`, code)} className={`${btn} bg-gold text-ink`}><Download size={13} /> Clickable ad</button>
        <button data-testid={`${p}-image-${base.id}`} onClick={() => downloadImage(ad)} className={`${btn} border border-white/15 hover:border-gold`}><ImageIcon size={13} /> Image</button>
        <button data-testid={`${p}-copy-${base.id}`} onClick={copy} className={`${btn} border border-white/15 hover:border-gold`}><Copy size={13} /> Code</button>
      </div>
      <p className="text-[10px] uppercase tracking-[0.2em] text-gold pt-1">Share to socials</p>
      {store && <CaptionEditor adKey={adKey} kind={kind} value={caption} onChange={setCaption} store={store} testPrefix={`${p}-${base.id}`} />}
      <ShareBar ad={ad} kind={kind} path={path} campaign={campaign} caption={fullCaption(caption)} testPrefix={`${p}-${base.id}`} />
    </div>
  );
};

export function CreatorAds({ kind = "creator", store }) {
  const k = KINDS[kind];
  const p = kind === "creator" ? "creator-ad" : "member-ad";
  const [src, setSrc] = useState({ source: "", campaign: "" });
  const url = adUrl(k.path, src.source, src.campaign, k.fallback);
  const set = (f) => (e) => setSrc({ ...src, [f]: e.target.value });
  return (
    <div data-testid={k.testId} className="rounded-2xl border border-gold/25 bg-surface p-5 space-y-5">
      <div>
        <p className="text-sm flex items-center gap-2"><Sparkles size={14} className="text-gold" /> {k.title}</p>
        <p className="text-[11px] text-text-muted mt-1">{k.note} "Clickable ad" and "Code" keep the button working. "Image" is the plain poster, so add the link yourself when you post it.</p>
      </div>
      <div className="grid sm:grid-cols-3 gap-4">
        <input data-testid={`${p}-source`} value={src.source} onChange={set("source")} className={input} placeholder="Where it runs, e.g. instagram" />
        <input data-testid={`${p}-campaign`} value={src.campaign} onChange={set("campaign")} className={input} placeholder={`Campaign, e.g. ${k.fallback}`} />
        <a data-testid={`${p}-test-link`} href={url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-xs text-gold self-end pb-2"><ExternalLink size={13} /> Test the button link</a>
      </div>
      <code data-testid={`${p}-url`} className="block text-[11px] text-text-muted break-all">{url}</code>
      <div className={`grid sm:grid-cols-2 ${k.ads.length > 2 ? "xl:grid-cols-3" : ""} gap-5`}>{k.ads.map((a) => <AdCard key={a.id} ad={a} url={url} kind={kind} p={p} path={k.path} campaign={src.campaign} store={store} />)}</div>
    </div>
  );
}
